import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { VehicleFilters } from '@bright/shared';

/**
 * The universal scope — one Area/ASM selection that follows the operator across
 * every analysis page.
 *
 * The owner's ask, verbatim: pool the network area-wise and ASM-wise, "and add a
 * selection filter universal throughout page". So this is app-level state, not
 * page state: pick "Chinhat" once and the dashboard, the stock lists, ageing and
 * analytics all read as Chinhat until it is cleared. It is deliberately **not**
 * in the URL — every page already has its own URL-borne filters, and a second
 * spelling of the same keys in the same query string would fight them. A page's
 * own control for the same key wins over the scope (`{...scope, ...pageFilters}`),
 * so drilling into one area from a scoped board still works.
 *
 * Persisted per browser: the scope an ASM sets in the morning is still on at
 * lunch. Vehicle search stays unscoped by design — finding one chassis must
 * never depend on which area filter someone left on.
 */

export interface GlobalScope {
  area?: string[];
  asmId?: string;
}

interface ScopeContextValue {
  scope: GlobalScope;
  setArea: (area: string[] | undefined) => void;
  setAsmId: (asmId: string | undefined) => void;
  clear: () => void;
  /** True when the scope narrows anything — the bar shows its clear affordance. */
  active: boolean;
}

const STORAGE_KEY = 'bsa.scope.v1';

function readStored(): GlobalScope {
  try {
    // The scope bar was retired at the owner's word (27 Aug). A previously
    // persisted scope would now be an invisible filter nobody can clear, so
    // stored scopes are dropped on read.
    window.localStorage.removeItem(STORAGE_KEY);
    const raw = null as string | null;
    if (!raw) return {};
    const parsed = JSON.parse(raw) as GlobalScope;
    const scope: GlobalScope = {};
    if (Array.isArray(parsed.area) && parsed.area.length > 0) {
      scope.area = parsed.area.filter((value): value is string => typeof value === 'string');
    }
    if (typeof parsed.asmId === 'string' && parsed.asmId !== '') scope.asmId = parsed.asmId;
    return scope;
  } catch {
    return {};
  }
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

export function ScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScope] = useState<GlobalScope>(readStored);

  useEffect(() => {
    try {
      if (scope.area?.length || scope.asmId) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // The scope simply will not survive a reload. Harmless.
    }
  }, [scope]);

  const setArea = useCallback((area: string[] | undefined) => {
    setScope((current) => {
      const next = { ...current };
      if (area && area.length > 0) next.area = area;
      else delete next.area;
      return next;
    });
  }, []);

  const setAsmId = useCallback((asmId: string | undefined) => {
    setScope((current) => {
      const next = { ...current };
      if (asmId) next.asmId = asmId;
      else delete next.asmId;
      return next;
    });
  }, []);

  const clear = useCallback(() => setScope({}), []);

  const value = useMemo<ScopeContextValue>(
    () => ({
      scope,
      setArea,
      setAsmId,
      clear,
      active: Boolean(scope.area?.length || scope.asmId),
    }),
    [scope, setArea, setAsmId, clear],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useGlobalScope(): ScopeContextValue {
  const context = useContext(ScopeContext);
  if (!context) throw new Error('useGlobalScope must be used inside ScopeProvider');
  return context;
}

/**
 * The scope as vehicle filters, ready to spread *under* a page's own filters:
 * `{ ...scopeFilters, ...pageFilters }` — the page's explicit choice wins.
 */
export function useScopeFilters(): VehicleFilters {
  const { scope } = useGlobalScope();
  return useMemo<VehicleFilters>(() => {
    const filters: VehicleFilters = {};
    if (scope.area?.length) filters.area = scope.area;
    if (scope.asmId) filters.asmId = scope.asmId;
    return filters;
  }, [scope]);
}
